import type { ReactElement, ReactNode } from "react";

import { Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import type {
	Chat,
	ChatMessage,
	ChatMessageWithUIState,
	SourceFile,
} from "@virtual-brain-lib/types";

import type { OpenArtifactParams } from "../artifact-panel/useArtifactPanel";
import { ToolCards } from "../tool-cards";
import MessageContent from "./core/MessageContent";
import MessageReasoningDropDown from "./core/MessageReasoningDropDown";
import MessageRetry from "./core/MessageRetry";
import UserMessageBubble from "./core/UserMessageBubble";
import MessageSuggestedFollowUps from "./follow-ups/MessageSuggestedFollowUps";
import useStreamedMessage from "./useStreamedMessage";

interface MessageProps {
	readonly children?: ReactElement;
	readonly isLastMessage: boolean;
	readonly onFollowUp?: (followUp: string) => Promise<void>;
	readonly onSourceSelected?: (params: SourceFile) => void;
	readonly onRetry?: (content: string) => Promise<void>;
	readonly message: ChatMessage;
	readonly openArtifact?: (params: OpenArtifactParams) => void;
	readonly actionsComponent?:
		| ReactNode
		| ((streamedMessage: ChatMessageWithUIState) => ReactNode);
	readonly thinkingComponent?: (streamedMessage: ChatMessageWithUIState) => ReactNode;
	readonly chatId: Chat["id"];
	readonly updateCachedMessage?: (
		messageId: string,
		updates: Partial<ChatMessageWithUIState>,
	) => void;
	readonly artifactCardBottomComponent?: (artifactId: string) => ReactElement | undefined;
	readonly userMessageCharLimit?: number;
	readonly isAnyMessageProcessing?: boolean;
}

function Message({
	children,
	isLastMessage,
	onFollowUp,
	onSourceSelected,
	onRetry,
	message,
	openArtifact,
	actionsComponent,
	thinkingComponent,
	chatId,
	updateCachedMessage,
	artifactCardBottomComponent,
	userMessageCharLimit,
	isAnyMessageProcessing,
}: MessageProps) {
	const streamedMessage = useStreamedMessage({
		message,
		chatId,
		openArtifact,
		isLastMessage,
		updateCachedMessage,
	});

	const isMessageProcessing = Boolean(streamedMessage.isThinking || streamedMessage.isAnswering);
	const isAssistant = streamedMessage.role === "assistant";
	const isUser = streamedMessage.role === "user";

	if (!streamedMessage.id) {
		return;
	}

	function getThinkingMode() {
		if (streamedMessage.webMode !== "forced") return "thinking";

		return "searching";
	}

	const messageBody = (
		<div className="flex w-full flex-col items-start gap-4">
			{isAssistant && <ToolCards message={streamedMessage} />}
			<div className="markdown-content w-full break-words">
				{streamedMessage.thinkingContent && isAssistant && (
					<MessageReasoningDropDown
						className="mb-3"
						reasoningContent={streamedMessage.thinkingContent}
						isThinking={!streamedMessage.content && isMessageProcessing}
						thinkingMode={getThinkingMode()}
						onSourceSelected={onSourceSelected}
					/>
				)}
				<MessageContent
					message={streamedMessage}
					isMessageProcessing={isMessageProcessing}
					isAnyMessageProcessing={isAnyMessageProcessing}
					openArtifact={openArtifact}
					artifactCardBottomComponent={artifactCardBottomComponent}
					onSourceSelected={onSourceSelected}
				>
					{children}
				</MessageContent>
			</div>

			{(streamedMessage.status === "failed" || message.status === "failed") && onRetry && (
				<MessageRetry
					isMessageProcessing={isMessageProcessing}
					onRetry={async () => onRetry(streamedMessage.content)}
				/>
			)}
		</div>
	);

	return (
		<div
			className={cn(
				"mb-6 flex w-full flex-col",
				isUser && "items-end",
				isLastMessage && "mb-12",
			)}
		>
			{isUser ? (
				<div className="group flex max-w-[80%] flex-col items-end gap-1">
					<UserMessageBubble
						content={streamedMessage.content}
						charLimit={userMessageCharLimit}
					>
						{messageBody}
					</UserMessageBubble>
					<div className="transition-opacity xl:opacity-0 xl:group-hover:opacity-100 xl:has-[[data-open]]:opacity-100">
						{typeof actionsComponent === "function"
							? actionsComponent(streamedMessage)
							: actionsComponent}
					</div>
				</div>
			) : (
				<>
					<Span variant="body-lg">{messageBody}</Span>
					{typeof actionsComponent === "function"
						? actionsComponent(streamedMessage)
						: actionsComponent}
				</>
			)}
			{onFollowUp && (
				<MessageSuggestedFollowUps
					message={streamedMessage}
					isLastMessage={isLastMessage}
					onFollowUp={onFollowUp}
				/>
			)}
			{thinkingComponent?.(streamedMessage)}
		</div>
	);
}

export default Message;
